import { useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { EXTERNAL_SOURCES, isExternalSource, type ExternalSource } from '@shared/services/externalSources'
import { useRemembered } from '../useRemembered'
import { PillPicker } from '../components/PillPicker'
import type { Market } from '@shared/services/amazonService'
import { AmazonPage } from './AmazonPage'
import { WebImagesPage } from './WebImagesPage'
import './amazon.css'
import './web-images.css'

/**
 * External Library (Huanrui, 2026-10-14)
 * -------------------------------------------------------------
 * Amazon, Google Images and Unsplash as one skill: the same page as each was
 * on its own, with a menu in front of the search box saying which. The menu
 * is the same one as Which Amazon (PillPicker), so it sits in the bar the
 * same way, dark in the middle of the empty page and white once docked.
 *
 * Changing the source keeps the page on screen, results and all; pressing
 * Search then opens the chosen source and searches the same words there
 * (Huanrui, 2026-10-14: no search on every switch -- two of the three cost a
 * request -- and no jump back to the empty page). What was picked stays with
 * the page it was picked on, and goes when the source changes. The last source chosen is
 * remembered; `?source=` opens a particular one (the old /amazon and
 * /google-images addresses land here).
 */
export function ExternalLibraryPage() {
  const [params, setParams] = useSearchParams()
  const [remembered, remember] = useRemembered<string>('ww.external.source', 'google')
  const asked = params.get('source')
  /** What the menu says. */
  const chosen: ExternalSource = isExternalSource(asked)
    ? asked
    : isExternalSource(remembered)
      ? remembered
      : 'google'

  /**
   * The page on screen. It stays when the menu changes, results and all,
   * until Search is pressed; then the chosen source opens and searches the
   * same words (Huanrui, 2026-10-14). A page that has searched nothing yet
   * simply gives way at once.
   */
  const [shown, setShown] = useState<ExternalSource>(chosen)
  const searched = useRef(false)
  /** The words in the box, and how many switches so far. */
  const lastTerm = useRef('')
  const [carry, setCarry] = useState({ term: '', round: 0, auto: false })
  /** The country, carried between Amazon and Google (2026-10-14). */
  const [market, setMarket] = useState<Market>('us')

  const open = (next: ExternalSource, term: string, auto: boolean) => {
    searched.current = auto
    setShown(next)
    setCarry((was) => ({ term, round: was.round + 1, auto }))
  }

  const choose = (next: ExternalSource) => {
    if (next === chosen) return
    remember(next)
    setParams({ source: next }, { replace: true })
    if (next === shown) return
    // Nothing on screen worth keeping yet: the chosen source opens now.
    if (!searched.current) open(next, lastTerm.current, false)
  }

  const lead = (
    <PillPicker value={chosen} options={EXTERNAL_SOURCES} onChange={choose} name="Which library" align="left" round />
  )
  const shared = {
    lead,
    barSource: chosen,
    market,
    onMarket: setMarket,
    initialTerm: carry.term,
    autoSearch: carry.auto,
    onTyped: (term: string) => {
      lastTerm.current = term
    },
    onSearched: () => {
      searched.current = true
    },
    // Another source chosen: Search takes the words there.
    onSubmitInstead: chosen !== shown ? (term: string) => open(chosen, term, true) : undefined,
  }
  const key = `${shown}-${carry.round}`

  return shown === 'amazon' ? (
    <AmazonPage key={key} {...shared} />
  ) : (
    <WebImagesPage key={key} source={shown} {...shared} />
  )
}

export default ExternalLibraryPage
